import React, { useState, useEffect } from 'react';
import { Tent, User, Calendar, MapPin, Activity, ChevronLeft, ChevronRight, AlertTriangle, Eye, CheckCircle, XCircle, Clock, Flag, X } from 'lucide-react';

const SiteReportsView = () => {
    const [reportsData, setReportsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        total: 0,
        limit: 25,
        offset: 0,
        has_more: false
    });
    const [filters, setFilters] = useState({
        site_id: '',
        site_name: '',
        username: '',
        status: '',
        priority: '',
        category: ''
    });
    const [showResolutionModal, setShowResolutionModal] = useState(false);
    const [resolutionData, setResolutionData] = useState({
        reportId: null,
        newStatus: '',
        resolutionNotes: ''
    });

    const fetchReportsData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                limit: pagination.limit,
                offset: pagination.offset,
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, value]) => value !== '')
                )
            });

            const response = await fetch(`/otgservices/getSiteReports.php?${params}`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.success) {
                setReportsData(data.data);
                setPagination(data.pagination);
                setError(null);
            } else {
                throw new Error(data.error || 'Failed to fetch site reports');
            }
        } catch (err) {
            console.error('Error fetching site reports:', err);
            setError(err.message);
            setReportsData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReportsData();
    }, [pagination.offset, pagination.limit]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setPagination(prev => ({ ...prev, offset: 0 })); // Reset to first page
    };

    const applyFilters = () => {
        setPagination(prev => ({ ...prev, offset: 0 }));
        fetchReportsData();
    };

    const clearFilters = () => {
        setFilters({
            site_id: '',
            site_name: '',
            username: '',
            status: '',
            priority: '',
            category: ''
        });
        setPagination(prev => ({ ...prev, offset: 0 }));
    };

    const handlePageChange = (direction) => {
        setPagination(prev => ({
            ...prev,
            offset: direction === 'next' 
                ? prev.offset + prev.limit 
                : Math.max(0, prev.offset - prev.limit)
        }));
    };

    const formatDateTime = (dateTimeString) => {
        return new Date(dateTimeString).toLocaleString();
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending':
                return <Clock className="w-4 h-4 text-yellow-600" />;
            case 'reviewed':
                return <Eye className="w-4 h-4 text-blue-600" />;
            case 'resolved':
                return <CheckCircle className="w-4 h-4 text-green-600" />;
            case 'dismissed':
                return <XCircle className="w-4 h-4 text-gray-600" />;
            default:
                return <Activity className="w-4 h-4 text-gray-600" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'reviewed':
                return 'bg-blue-100 text-blue-800';
            case 'resolved':
                return 'bg-green-100 text-green-800';
            case 'dismissed':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'urgent':
                return <Flag className="w-4 h-4 text-red-600" />;
            case 'high':
                return <Flag className="w-4 h-4 text-orange-600" />;
            case 'medium':
                return <Flag className="w-4 h-4 text-blue-600" />;
            case 'low':
                return <Flag className="w-4 h-4 text-gray-600" />;
            default:
                return <Flag className="w-4 h-4 text-gray-600" />;
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'urgent':
                return 'bg-red-100 text-red-800';
            case 'high':
                return 'bg-orange-100 text-orange-800';
            case 'medium':
                return 'bg-blue-100 text-blue-800';
            case 'low':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const updateReportStatus = async (reportId, newStatus, resolutionNotes = null) => {
        try {
            const response = await fetch('/otgservices/updateSiteReportStatus.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    report_id: reportId,
                    status: newStatus,
                    resolution_notes: resolutionNotes
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update status');
            }

            const result = await response.json();

            if (result.success) {
                // Update the local data to reflect the change
                setReportsData(prevData => 
                    prevData.map(report => 
                        report.report_id === reportId 
                            ? { 
                                ...report, 
                                status: newStatus,
                                resolution_notes: resolutionNotes || report.resolution_notes 
                              }
                            : report
                    )
                );
                return true;
            } else {
                throw new Error(result.error || 'Failed to update status');
            }
        } catch (error) {
            console.error('Error updating report status:', error);
            alert('Error updating status: ' + error.message);
            return false;
        }
    };

    const handleStatusChange = async (reportId, newStatus) => {
        // If changing to a status that might need resolution notes, show modal
        if (['resolved', 'reviewed', 'dismissed'].includes(newStatus)) {
            setResolutionData({
                reportId,
                newStatus,
                resolutionNotes: ''
            });
            setShowResolutionModal(true);
        } else {
            // For pending status, no resolution notes needed
            if (window.confirm(`Are you sure you want to change this report status to "${newStatus}"?`)) {
                await updateReportStatus(reportId, newStatus);
            }
        }
    };

    const handleResolutionSubmit = async () => {
        const { reportId, newStatus, resolutionNotes } = resolutionData;
        const success = await updateReportStatus(reportId, newStatus, resolutionNotes);
        
        if (success) {
            setShowResolutionModal(false);
            setResolutionData({
                reportId: null,
                newStatus: '',
                resolutionNotes: ''
            });
        }
    };

    const handleResolutionCancel = () => {
        setShowResolutionModal(false);
        setResolutionData({
            reportId: null,
            newStatus: '',
            resolutionNotes: ''
        });
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-orange-600 text-white px-6 py-4">
                <div className="mx-auto px-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-white p-2 rounded">
                            <Tent className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold">OTG Camp</h1>
                            <p className="text-sm opacity-90">SITE REPORTS</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <span className="text-sm bg-orange-700 px-2 py-1 rounded">Pro</span>
                    </div>
                </div>
            </div>

            <div className="mx-auto p-6">
                {/* Filters */}
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <h2 className="text-lg font-semibold mb-4">Filters</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 xl:grid-cols-6 2xl:grid-cols-6 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Site ID
                            </label>
                            <input
                                type="number"
                                value={filters.site_id}
                                onChange={(e) => handleFilterChange('site_id', e.target.value)}
                                placeholder="Enter site ID"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Site Name
                            </label>
                            <input
                                type="text"
                                value={filters.site_name}
                                onChange={(e) => handleFilterChange('site_name', e.target.value)}
                                placeholder="Enter site name"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Username
                            </label>
                            <input
                                type="text"
                                value={filters.username}
                                onChange={(e) => handleFilterChange('username', e.target.value)}
                                placeholder="Enter username"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Status
                            </label>
                            <select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            >
                                <option value="">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="reviewed">Reviewed</option>
                                <option value="resolved">Resolved</option>
                                <option value="dismissed">Dismissed</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Priority
                            </label>
                            <select
                                value={filters.priority}
                                onChange={(e) => handleFilterChange('priority', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            >
                                <option value="">All Priorities</option>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Category
                            </label>
                            <select
                                value={filters.category}
                                onChange={(e) => handleFilterChange('category', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            >
                                <option value="">All Categories</option>
                                <option value="Road Conditions">Road Conditions</option>
                                <option value="Site Accessibility">Site Accessibility</option>
                                <option value="Facilities Issue">Facilities Issue</option>
                                <option value="Safety Concern">Safety Concern</option>
                                <option value="Environmental Impact">Environmental Impact</option>
                                <option value="Maintenance Needed">Maintenance Needed</option>
                                <option value="Information Update">Information Update</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex gap-3 mt-4">
                        <button
                            onClick={applyFilters}
                            className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                            Apply Filters
                        </button>
                        <button
                            onClick={clearFilters}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        >
                            Clear All
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-800">
                            Site Reports
                            {pagination.total > 0 && (
                                <span className="text-sm text-gray-500 ml-2">
                                    ({pagination.total} total reports)
                                </span>
                            )}
                        </h2>
                    </div>

                    {loading && (
                        <div className="flex justify-center items-center h-48">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
                        </div>
                    )}

                    {error && (
                        <div className="p-6 text-center text-red-600">
                            <p>Error: {error}</p>
                            <button
                                onClick={fetchReportsData}
                                className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {!loading && !error && reportsData.length === 0 && (
                        <div className="p-6 text-center text-gray-500">
                            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                            <p>No site reports found.</p>
                        </div>
                    )}

                    {!loading && !error && reportsData.length > 0 && (
                        <div className="overflow-x-auto w-full">
                            <table className="w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            ID
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <div className="flex items-center gap-1">
                                                <Calendar className="w-4 h-4" />
                                                Date/Time
                                            </div>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Priority
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <div className="flex items-center gap-1">
                                                <MapPin className="w-4 h-4" />
                                                Site
                                            </div>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <div className="flex items-center gap-1">
                                                <User className="w-4 h-4" />
                                                Reporter
                                            </div>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Category
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Subject
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Message
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Resolution Notes
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {reportsData.map((report) => (
                                        <tr key={report.report_id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                #{report.report_id}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {formatDateTime(report.created_at)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    {getStatusIcon(report.status)}
                                                    <select
                                                        value={report.status}
                                                        onChange={(e) => handleStatusChange(report.report_id, e.target.value)}
                                                        className={`text-xs font-medium border-none rounded-full px-2.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-orange-500 ${getStatusColor(report.status)}`}
                                                    >
                                                        <option value="pending">pending</option>
                                                        <option value="reviewed">reviewed</option>
                                                        <option value="resolved">resolved</option>
                                                        <option value="dismissed">dismissed</option>
                                                    </select>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    {getPriorityIcon(report.priority)}
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(report.priority)}`}>
                                                        {report.priority}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <div>
                                                    <div className="font-medium">{report.site_name}</div>
                                                    <div className="text-gray-500">
                                                        ID: {report.site_location}
                                                        {report.site_type && ` • ${report.site_type}`}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {report.reporter_username}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                                                    {report.category}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                <div className="max-w-sm break-words whitespace-normal font-medium">
                                                    {report.subject}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                <div className="max-w-lg break-words whitespace-normal">
                                                    {report.message}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                <div className="max-w-md break-words whitespace-normal">
                                                    {report.resolution_notes ? (
                                                        <div className="bg-gray-50 p-2 rounded text-xs">
                                                            {report.resolution_notes}
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 italic text-xs">No notes</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && !error && pagination.total > 0 && (
                        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                            <div className="text-sm text-gray-500">
                                Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} reports
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handlePageChange('prev')}
                                    disabled={pagination.offset === 0}
                                    className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm ${
                                        pagination.offset === 0
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Previous
                                </button>
                                <button
                                    onClick={() => handlePageChange('next')}
                                    disabled={!pagination.has_more}
                                    className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm ${
                                        !pagination.has_more
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Resolution Notes Modal */}
            {showResolutionModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-800">
                                Update Report Status
                            </h3>
                            <button
                                onClick={handleResolutionCancel}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="mb-4">
                            <p className="text-sm text-gray-600 mb-2">
                                Changing status to: <span className="font-medium text-gray-800">{resolutionData.newStatus}</span>
                            </p>
                            
                            <label htmlFor="resolutionNotes" className="block text-sm font-medium text-gray-700 mb-1">
                                Resolution Notes {resolutionData.newStatus === 'resolved' ? '*' : '(Optional)'}
                            </label>
                            <textarea
                                id="resolutionNotes"
                                value={resolutionData.resolutionNotes}
                                onChange={(e) => setResolutionData(prev => ({
                                    ...prev,
                                    resolutionNotes: e.target.value
                                }))}
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                                placeholder={
                                    resolutionData.newStatus === 'resolved' 
                                        ? "Describe how this issue was resolved..."
                                        : resolutionData.newStatus === 'reviewed'
                                        ? "Add review comments..."
                                        : "Explain why this report was dismissed..."
                                }
                            />
                            {resolutionData.newStatus === 'resolved' && !resolutionData.resolutionNotes.trim() && (
                                <p className="mt-1 text-xs text-red-600">Resolution notes are required when marking as resolved</p>
                            )}
                        </div>

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={handleResolutionCancel}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleResolutionSubmit}
                                disabled={resolutionData.newStatus === 'resolved' && !resolutionData.resolutionNotes.trim()}
                                className={`px-4 py-2 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                                    resolutionData.newStatus === 'resolved' && !resolutionData.resolutionNotes.trim()
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-orange-600 text-white hover:bg-orange-700'
                                }`}
                            >
                                Update Status
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SiteReportsView;